import { useEffect, useState, useCallback } from 'react';
import {
  Box, Card, Stack, Typography, CircularProgress, Button, Switch,
  Table, Sheet, Modal, ModalOverflow, ModalDialog, ModalClose,
} from '@mui/joy';
import {
  APIFinanceReceivables, APIFinanceReceivablesDetails, APIFinanceOperations, APIFinanceOperationReconcile,
} from '../../../api';
import {
  useFinanceToken, useIsMobile, PeriodBar, MetricCard, MetricGrid, SectionCard, scrollTableSx,
  fmtMoney, fmtNum,
} from './financeShared';

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString('ru-RU') : '');

export default function FinanceReceivablesTab() {
  const token = useFinanceToken();
  const isMobile = useIsMobile();
  const [dateFrom, setDateFrom] = useState(''); // по умолчанию — вся дебиторка (без периода)
  const [dateTo, setDateTo] = useState('');
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const [selClient, setSelClient] = useState(null);
  const [details, setDetails] = useState({ results: [], pagination: {} });
  const [detailsPage, setDetailsPage] = useState(1);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const [reconcile, setReconcile] = useState({ open: false, row: null });

  const baseParams = () => ({
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    overdue_only: overdueOnly ? 'true' : undefined,
  });

  const loadSummary = useCallback(() => {
    if (!token) return;
    setLoading(true);
    APIFinanceReceivables(baseParams(), token)
      .then(setSummary).catch((e) => console.error('receivables', e)).finally(() => setLoading(false));
  }, [token, dateFrom, dateTo, overdueOnly]);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  const loadDetails = useCallback((clientKey, page) => {
    if (!token) return;
    setDetailsLoading(true);
    APIFinanceReceivablesDetails({ ...baseParams(), client_key: clientKey, page }, token)
      .then((d) => setDetails(d || { results: [], pagination: {} }))
      .catch((e) => console.error('receivables details', e))
      .finally(() => setDetailsLoading(false));
  }, [token, dateFrom, dateTo, overdueOnly]);

  // при смене фильтров закрываем детали (чтобы не показывать устаревшее)
  useEffect(() => { setSelClient(null); setDetails({ results: [], pagination: {} }); }, [dateFrom, dateTo, overdueOnly]);
  useEffect(() => { if (selClient) loadDetails(selClient.key, detailsPage); }, [detailsPage]);

  const openClient = (c) => { setSelClient(c); setDetailsPage(1); loadDetails(c.key, 1); };
  const t = summary?.totals || {};

  return (
    <Box>
      <PeriodBar
        from={dateFrom} to={dateTo}
        onFrom={setDateFrom} onTo={setDateTo}
        onPreset={(f, tt) => { setDateFrom(f); setDateTo(tt); }}
        loading={loading}
        right={
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Typography level="body-sm" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>Только просроченные</Typography>
            <Switch checked={overdueOnly} onChange={(e) => setOverdueOnly(e.target.checked)} />
          </Stack>
        }
      />

      <MetricGrid>
        <MetricCard index={0} title="Всего должны" value={fmtMoney(t.outstanding)} color="#0ea5e9" />
        <MetricCard index={1} title="Просрочено (по сроку)" value={fmtMoney(t.overdue)} color="#ef4444" />
        <MetricCard index={2} title="Просрочено > 30 дней" value={fmtMoney(t.overdue_30)} color="#b91c1c" />
        <MetricCard index={3} title="DSO (ждём оплату)" value={summary ? `${fmtNum(summary.dso_avg_days)} дн.` : '—'} color="#6366f1" />
        <MetricCard index={4} title="Должников" value={fmtNum(t.debtors_count)} color="#8b5cf6" />
      </MetricGrid>

      {/* По клиентам */}
      <SectionCard title="По клиентам">
        <Sheet variant="outlined" sx={{ borderRadius: 'lg', overflow: 'auto' }}>
          <Table borderAxis="both" stickyHeader hoverRow sx={scrollTableSx(760)}>
            <thead>
              <tr>
                <th>Клиент</th>
                <th style={{ width: 150, textAlign: 'right' }}>Долг, ₽</th>
                <th style={{ width: 150, textAlign: 'right' }}>Просрочено, ₽</th>
                <th style={{ width: 100, textAlign: 'center' }}>Сделок</th>
                <th style={{ width: 130, textAlign: 'center' }}>Макс. возраст</th>
                <th style={{ width: 110, textAlign: 'center' }}></th>
              </tr>
            </thead>
            <tbody>
              {(summary?.by_client || []).map((c) => (
                <tr key={c.key}>
                  <td>{c.name}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmtMoney(c.outstanding)}</td>
                  <td style={{ textAlign: 'right', color: c.overdue > 0 ? '#ef4444' : undefined }}>{fmtMoney(c.overdue)}</td>
                  <td style={{ textAlign: 'center' }}>{c.deals_count}</td>
                  <td style={{ textAlign: 'center' }}>{c.oldest_age_days} дн.</td>
                  <td style={{ textAlign: 'center' }}>
                    <Button size="sm" variant="soft" onClick={() => openClient(c)}>Детали</Button>
                  </td>
                </tr>
              ))}
              {(!summary?.by_client || summary.by_client.length === 0) && (
                <tr><td colSpan={6} style={{ textAlign: 'center', color: '#94a3b8' }}>Нет дебиторки</td></tr>
              )}
            </tbody>
          </Table>
        </Sheet>
      </SectionCard>

      {/* Детали по выбранному клиенту — модалка */}
      <Modal open={!!selClient} onClose={() => { setSelClient(null); setDetails({ results: [], pagination: {} }); }}>
        <ModalDialog
          layout={isMobile ? 'fullscreen' : 'center'}
          sx={{
            width: isMobile ? '100%' : '92%', maxWidth: isMobile ? '100%' : 960,
            maxHeight: isMobile ? '100%' : '90dvh', borderRadius: isMobile ? 0 : 'xl',
            p: { xs: 1.5, sm: 2.5 }, display: 'flex', flexDirection: 'column', overflow: 'hidden',
          }}
        >
          <ModalClose onClick={() => { setSelClient(null); setDetails({ results: [], pagination: {} }); }} />
          <Typography level="title-md" sx={{ mb: 1, pr: 4, fontWeight: 700 }}>Сделки: {selClient?.name}</Typography>
          {detailsLoading && <CircularProgress size="sm" sx={{ mb: 1 }} />}
          <Sheet variant="outlined" sx={{ borderRadius: 'lg', overflow: 'auto', flex: 1, minHeight: 0 }}>
            <Table borderAxis="both" size="sm" hoverRow stickyHeader sx={scrollTableSx(820)}>
              <thead>
                <tr>
                  <th style={{ width: 90 }}>Сделка</th>
                  <th>Адрес</th>
                  <th style={{ width: 100 }}>Завершено</th>
                  <th style={{ width: 120, textAlign: 'right' }}>Сумма</th>
                  <th style={{ width: 120, textAlign: 'right' }}>Получено</th>
                  <th style={{ width: 120, textAlign: 'right' }}>Долг</th>
                  <th style={{ width: 90, textAlign: 'center' }}>Возраст</th>
                  <th style={{ width: 140, textAlign: 'center' }}></th>
                </tr>
              </thead>
              <tbody>
                {(details.results || []).map((r) => (
                  <tr key={r.event_id}>
                    <td>#{r.deal_id}</td>
                    <td>{r.address || r.city || '—'}</td>
                    <td>{fmtDate(r.date_completed)}</td>
                    <td style={{ textAlign: 'right' }}>{fmtMoney(r.recognized)}</td>
                    <td style={{ textAlign: 'right' }}>{fmtMoney(r.received)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmtMoney(r.outstanding)}</td>
                    <td style={{ textAlign: 'center', color: r.is_overdue ? '#ef4444' : undefined }}>{r.age_days} дн.</td>
                    <td style={{ textAlign: 'center' }}>
                      <Button size="sm" variant="soft" color="success" onClick={() => setReconcile({ open: true, row: r })}>Привязать оплату</Button>
                    </td>
                  </tr>
                ))}
                {(!details.results || details.results.length === 0) && !detailsLoading && (
                  <tr><td colSpan={8} style={{ textAlign: 'center', color: '#94a3b8' }}>Нет сделок с долгом</td></tr>
                )}
              </tbody>
            </Table>
          </Sheet>
          {details.pagination?.total_pages > 1 && (
            <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mt: 1 }}>
              <Button size="sm" variant="outlined" disabled={!details.pagination.previous} onClick={() => setDetailsPage((p) => Math.max(1, p - 1))}>Назад</Button>
              <Typography level="body-sm">{details.pagination.current_page} / {details.pagination.total_pages}</Typography>
              <Button size="sm" variant="outlined" disabled={!details.pagination.next} onClick={() => setDetailsPage((p) => p + 1)}>Вперёд</Button>
            </Stack>
          )}
        </ModalDialog>
      </Modal>
      
      <ReconcileModal
        state={reconcile} token={token}
        onClose={() => setReconcile({ open: false, row: null })}
        onDone={() => { setReconcile({ open: false, row: null }); loadSummary(); if (selClient) loadDetails(selClient.key, detailsPage); }}
      />
    </Box>
  );
}

// ── Модалка привязки входящего перевода к сделке ──
function ReconcileModal({ state, token, onClose, onDone }) {
  const [ops, setOps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!state.open || !token) return;
    setLoading(true);
    APIFinanceOperations({ direction: 'in', is_reconciled: 'false', page_size: 100 }, token)
      .then((d) => setOps((d?.results) || []))
      .catch((e) => console.error('unreconciled ops', e))
      .finally(() => setLoading(false));
  }, [state.open, token]);

  const link = (op) => {
    const row = state.row;
    setBusyId(op.id);
    APIFinanceOperationReconcile(op.id, { event_id: row.event_id, bitrix_deal_id: row.deal_id }, token)
      .then(() => onDone())
      .catch((e) => console.error('reconcile', e))
      .finally(() => setBusyId(null));
  };

  return (
    <Modal open={state.open} onClose={onClose}>
      <ModalOverflow>
        <ModalDialog sx={{ minWidth: 380, maxWidth: 560 }}>
          <ModalClose onClick={onClose} />
          <Typography level="title-md" sx={{ mb: 0.5 }}>Привязать поступление</Typography>
          <Typography level="body-xs" sx={{ color: 'text.secondary', mb: 1.5 }}>
            Сделка #{state.row?.deal_id}, долг {fmtMoney(state.row?.outstanding)}. Выберите входящую операцию (перевод), чтобы закрыть дебиторку.
          </Typography>
          {loading && <CircularProgress size="sm" />}
          {!loading && ops.length === 0 && (
            <Typography level="body-sm" sx={{ color: 'text.tertiary', py: 2 }}>
              Нет непривязанных входящих операций. Сначала занесите поступление во вкладке «ДДС».
            </Typography>
          )}
          <Stack spacing={1}>
            {ops.map((op) => (
              <Card key={op.id} variant="soft" sx={{ p: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography level="body-sm" sx={{ fontWeight: 600 }}>{fmtMoney(op.amount)}</Typography>
                    <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                      {fmtDate(op.operation_date)} · {op.counterparty || op.comment || op.account_name || '—'}
                    </Typography>
                  </Box>
                  <Button size="sm" loading={busyId === op.id} onClick={() => link(op)}>Привязать</Button>
                </Stack>
              </Card>
            ))}
          </Stack>
        </ModalDialog>
      </ModalOverflow>
    </Modal>
  );
}